import { Paper, Typography, Stack, Divider, Chip, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../ProgramForm/helpers/quotationCalculations";

const ProgramPricing = ({ program }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;

    // Helper function to get the correct price and currency
    const getDisplayPrice = () => {
        // According to phase-3-invoices-frontend-ar.md, invoice currency comes from program.currency
        const currency = program.currency;
        return {
            symbol: currency?.symbol || '฿',
            name: currency?.name?.[lang] || currency?.name?.en || 'THB'
        };
    };

    const { symbol } = getDisplayPrice();

    return (
        <Paper
            className="pricing-section program-price-element"
            sx={{
                p: 3,
                mt: 3,
                '@media print': {
                    pageBreakInside: 'auto',
                    marginTop: '20px',
                }
            }}
        >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                {lang === 'ar' ? "ملخص الأسعار" : "Pricing Summary"}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
                {/* Subtotal */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {lang === 'ar' ? "المجموع الفرعي" : "Subtotal"}
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                        {symbol} {formatCurrency(program.subtotal || program.sub_total || program.subtotal_selling_price || program.sell_subtotal || program.invoice?.subtotal || program.invoice?.sub_total || ((parseFloat(program.price_before_tax) || 0) + (parseFloat(program.sell_discount_amount) || 0)))}
                    </Typography>
                </Stack>

                {/* Discount */}
                {(program.sell_discount_amount > 0 || program.discount_type !== "zero") && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="error.main">
                            {lang === 'ar' ? "الخصم" : "Discount"}
                        </Typography>
                        <Typography variant="h6" fontWeight="600" color="error.main">
                            - {symbol} {formatCurrency(program.sell_discount_amount || 0)}
                        </Typography>
                    </Stack>
                )}

                {/* Price Before Tax */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {lang === 'ar' ? "السعر قبل الضريبة" : "Price Before Tax"}
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                        {symbol} {formatCurrency(program.price_before_tax || 0)}
                    </Typography>
                </Stack>

                {/* Tax */}
                {(program.tax_amount > 0) && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {lang === 'ar' ? "الضريبة" : "Tax"}
                            </Typography>
                            {program.tax_percentage > 0 && (
                                <Chip label={`${program.tax_percentage}%`} size="small" variant="outlined" />
                            )}
                        </Box>
                        <Typography variant="h6" fontWeight="600">
                            + {symbol} {formatCurrency(program.tax_amount || 0)}
                        </Typography>
                    </Stack>
                )}

                <Divider />

                {/* Final Price / Total Due */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                        {lang === 'ar' ? "المجموع المستحق" : "Total Due"}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        {symbol} {formatCurrency(program.price || 0)}
                    </Typography>
                </Stack>

            </Stack>
        </Paper>
    );
};

export default ProgramPricing;
